import { Component, OnInit } from "@angular/core";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { NGXLogger } from "ngx-logger";
import { MenuItem, MessageService } from "primeng/api";
import { AutoCompleteCompleteEvent } from "primeng/autocomplete";

import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from "primeng/dynamicdialog";
import { Subject } from "rxjs";
import { ComuneDTO } from "src/app/models/comune.model";
import { PreventivoFase1DTO } from "src/app/models/dto/preventivoFase1.model";
import { SopralluogoFase1DTO } from "src/app/models/dto/sopralluogoFase1.model";
import { Impresa } from "src/app/models/impresa.model";
import { ModificaPraticaDTO } from "src/app/models/modificaPratica.scuola.model";
import { Pratica } from "src/app/models/pratica.model";
import { PraticaScuola } from "src/app/models/pratica.scuola.model";
import { PraticaFlowdy2_Firestore } from "src/app/models/praticaFirestore.model";
import { ScuolaDTO } from "src/app/models/scuola.model";
import { ComuniService } from "src/app/services/comuni.service";
import { GoogleStorageService } from "src/app/services/googleStorage.service";
import { ImpreseService } from "src/app/services/imprese.service";
import { PraticheScuolaService } from "src/app/services/pratiche.scuola.service";
import { PraticheService } from "src/app/services/pratiche.service";
import { environment } from "src/environments/environment";
import { DialogModificaPosizioneBando1 } from "../../../scuola/bando1/pratiche-scuola-bando1.component";
import { PosizioneScuola } from "../posizione-scuola/posizione-scuola.component";
import { ModificaPosizioneScuola } from "../modifica-posizione-scuola/modifica-posizione-scuola.component";
import { CentraliService } from "src/app/services/centrali.service";
import { Centrale } from "src/app/models/centrale.models";
import { ListeService } from "src/app/services/liste.service";

interface TipoSede {
  id?: number;
  codice?: string;
  value?: string;
  label?: string;
}

interface TipoBackup {
  id: number;
  codice: string;
}

@Component({
  selector: "component-modifica-pratica",
  templateUrl: "./dialog-modifica-pratica.component.html",
  styleUrls: ["../shared.component.css"],
})
export class ModificaPratica implements OnInit {
  protected _onDestroy = new Subject<void>();
  public pratica: Pratica;
  public praticaFromFirestore: PraticaFlowdy2_Firestore;
  private tipiSede: TipoSede[];
  private tipiBackup: TipoBackup[];
  protected centraliLoaded: boolean = false;

  protected pagineModificaPratica: MenuItem[];
  protected activeIndex: number = 0;

  protected dettagliForm: FormGroup;
  protected scuolaForm: FormGroup;
  protected centraleForm: FormGroup;

  protected localizzazioniForm: FormGroup;
  private scuola: ScuolaDTO;

  protected ref: DynamicDialogRef;

  private collection_pratiche_scuola: string =
    environment.firebase.collection_pratiche;

  protected comuni: ComuneDTO[] = [];
  protected aziende: Impresa[] = [];
  protected centrali: Centrale[] = [];
  protected filteredComuni: any[] = [];
  protected filteredAziende: any[] = [];
  protected filteredCentrali: any[] = [];
  protected comuniLoaded: boolean = false;

  constructor(
    private logger: NGXLogger,
    public dialogRef: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private _formBuilder: FormBuilder,
    private praticheScuolaService: PraticheService,
    private messageService: MessageService,
    public dialogService: DialogService,
    private firestoreService: AngularFirestore,
    private comuniService: ComuniService,
    private aziendeService: ImpreseService,
    private centraliService: CentraliService,
    private listeService: ListeService,
  ) {
    this.pratica = this.config.data.pratica;
    this.praticaFromFirestore = this.config.data.praticaFromFirestore;
    this.scuola = this.config.data.scuola;

    this.tipiSede =
      this.pratica.bando.denominazione === "BANDO 1"
        ? [
            { id: 7, codice: "Mandatoria A" },
            { id: 8, codice: "Mandatoria B" },
            { id: 9, codice: "Mandatoria C" },
            { id: 10, codice: "Mandatoria D" },
            { id: 11, codice: "Aggiuntiva A" },
            { id: 12, codice: "Aggiuntiva B" },
            { id: 13, codice: "Aggiuntiva C" },
            { id: 14, codice: "Aggiuntiva D" },
          ]
        : this.listeService.opzioniTipoSedeBando2.map((item) => ({
            id: item.id,
            codice: item.label,
          }));

    this.logger.info(
      "[MODIFICA PRATICA] Tipi sede disponibili:",
      this.tipiSede,
    );
    this.logger.info(
      "[MODIFICA PRATICA] Pratica input (codiceInvio/tipoSedeGara/tipoSedeEffettiva):",
      {
        codiceInvio: this.pratica?.codiceInvio,
        tipoSedeGara: this.pratica?.tipoSedeGara,
        tipoSedeEffettiva: this.pratica?.tipoSedeEffettiva,
        bando: this.pratica?.bando?.denominazione,
      },
    );

    this.tipiBackup = [
      { id: 1, codice: "FO" },
      { id: 2, codice: "LTE" },
      { id: 3, codice: "NESSUNO" },
    ];
  }

  getTipoBackup(tipologia: String): number {
    switch (tipologia) {
      case "FO":
        return 0;
      case "LTE":
        return 1;
      case "NESSUNO":
        return 2;
      default:
        return null;
    }
  }

  private getTipoSedeCode(value: unknown): string | null {
    if (typeof value === "string") {
      return value;
    }
    if (value && typeof value === "object" && "codice" in value) {
      const codice = (value as { codice?: unknown }).codice;
      return typeof codice === "string" ? codice : null;
    }
    return null;
  }

  private getTipoSedeOption(value: unknown): TipoSede | null {
    const code = this.getTipoSedeCode(value);
    if (!code) {
      return null;
    }
    return this.tipiSede?.find((t) => t.codice === code) ?? null;
  }

  ngOnInit(): void {
    this.logger.info(this.pratica);
    this.loadComuni();
    this.loadImprese();
    this.loadCentrali();
    this.inizializzaFormGroups();
    this.inizializzaSteps();
  }

  inizializzaFormGroups() {
    const tipoSedeGaraCode = this.getTipoSedeCode(this.pratica?.tipoSedeGara);
    const tipoSedeEffettivaCode = this.getTipoSedeCode(
      this.pratica?.tipoSedeEffettiva,
    );
    const matchTipoSede = this.getTipoSedeOption(this.pratica?.tipoSedeGara);
    const matchTipoSedeEffettiva = this.getTipoSedeOption(
      this.pratica?.tipoSedeEffettiva,
    );
    /*   this.logger.info("[MODIFICA PRATICA] Diagnostica valorizzazione dropdown sede:", {
      formTipoSedeValue: this.pratica?.tipoSedeGara,
      formTipoSedeEffettivaValue: this.pratica?.tipoSedeEffettiva,
      formTipoSedeCode: tipoSedeGaraCode,
      formTipoSedeEffettivaCode: tipoSedeEffettivaCode,
      matchTipoSede,
      matchTipoSedeEffettiva,
    }); */

    this.dettagliForm = this._formBuilder.group({
      codiceInvio: [this.pratica.codiceInvio],
      codiceInfratel: new FormControl(
        { value: this.pratica.cui, disabled: true },
        Validators.required,
      ),
      tipoSede: [matchTipoSede, [Validators.required]],
      tipoSedeEffettiva: [matchTipoSedeEffettiva, [Validators.required]],
      //colocata: [null, [Validators.required]],
      azienda: [this.pratica.azienda ? this.pratica.azienda : null],
      tipoBackup: [
        this.scuola.tipoBackup
          ? this.tipiBackup[this.getTipoBackup(this.scuola.tipoBackup)]
          : this.tipiBackup[this.getTipoBackup("NESSUNO")],
        [Validators.required],
      ],
      backup: [
        this.praticaFromFirestore.backup ? true : false,
        this.pratica.bando.denominazione === "BANDO 2"
          ? [Validators.required]
          : null,
      ],
      wifi: [this.scuola.wifi ? true : false, [Validators.required]],
      numeroAccessPoint: [
        this.pratica.numeroAccessPoint ? this.pratica.numeroAccessPoint : null,
        this.pratica.wifi ? [Validators.required] : null,
      ],
      //lavorabile: [this.pratica.lavorabile, [Validators.required]],
    });
    /*   this.logger.info(
      "[MODIFICA PRATICA] DettagliForm inizializzato (tipoSede/tipoSedeEffettiva):",
      {
        tipoSedeControlValue: this.dettagliForm.get("tipoSede")?.value,
        tipoSedeEffettivaControlValue:
          this.dettagliForm.get("tipoSedeEffettiva")?.value,
      },
    ); */
    let comuneCentrale: ComuneDTO = new ComuneDTO();
    comuneCentrale.denominazione = this.pratica.comuneCentrale;

    this.scuolaForm = this._formBuilder.group({
      denominazioneScuola: [this.scuola.denominazione],
      codiceMiur: [this.scuola.codiceMiur, [Validators.required]],
      indirizzoScuola: [this.scuola.indirizzo, [Validators.required]],
      comuneScuola: [this.scuola.comune, [Validators.required]],
      latitudineScuola: [
        this.praticaFromFirestore.coordinateScuola
          ? this.praticaFromFirestore.coordinateScuola.latitude
          : null,
        [Validators.required],
      ],
      longitudineScuola: [
        this.praticaFromFirestore.coordinateScuola
          ? this.praticaFromFirestore.coordinateScuola.longitude
          : null,
        [Validators.required],
      ],
    });

    this.centraleForm = this._formBuilder.group({
      idCentrale: new FormControl(
        {
          value: this.pratica.centrale ? this.pratica.centrale.clli : null,
          disabled: true,
        },
        Validators.required,
      ),
      nomeCentrale: new FormControl(
        {
          value: this.pratica.centrale
            ? this.pratica.centrale.denominazione
            : null,
          disabled: true,
        },
        Validators.required,
      ),
      indirizzoCentrale: new FormControl(
        {
          value: this.pratica.centrale ? this.pratica.centrale.indirizzo : null,
          disabled: true,
        },
        Validators.required,
      ),
      comuneCentrale: new FormControl(
        {
          value: this.pratica.centrale ? this.pratica.centrale.comune : null,
          disabled: true,
        },
        Validators.required,
      ),
      latitudineCentrale: new FormControl(
        {
          value: this.pratica.centrale
            ? this.pratica.centrale.latitudine
            : null,
          disabled: true,
        },
        Validators.required,
      ),
      longitudineCentrale: new FormControl(
        {
          value: this.pratica.centrale
            ? this.pratica.centrale.longitudine
            : null,
          disabled: true,
        },
        Validators.required,
      ),
    });
  }
  inizializzaSteps() {
    this.pagineModificaPratica = [
      {
        label: "Dettagli",
      },
      {
        label: "Scuola",
      },
      {
        label: "Centrale",
      },
    ];
    this.activeIndex = 0;
  }

  checkNextStep(): boolean {
    let disableNextStepButton = false;
    if (this.activeIndex === this.pagineModificaPratica.length - 1) {
      disableNextStepButton = true;
    } else if (this.activeIndex === 0 && !this.dettagliForm.valid) {
      disableNextStepButton = true;
    } else if (this.activeIndex === 1 && !this.scuolaForm.valid) {
      disableNextStepButton = true;
    } else if (this.activeIndex === 2 && !this.centraleForm.valid) {
      disableNextStepButton = true;
    }

    return disableNextStepButton;
  }

  changePage() {
    this.activeIndex = this.activeIndex + 1;
  }

  openMappaScuola() {
    this.logger.info("---OPENMAPPA SCUOLA----");
    this.ref = this.dialogService.open(ModificaPosizioneScuola, {
      data: {
        pratica: this.praticaFromFirestore,
        isScuola: true,
      },
      header:
        "Modifica la posizione della scuola " + this.praticaFromFirestore.cui,
      width: "70%",
      height: "95%",
      baseZIndex: 10000,
      closable: true,
      draggable: false,
    });
    this.ref.onClose.subscribe((changeResult) => {
      this.logger.info("CHIUDO MAPPA " + changeResult);
      const collectionRef = this.firestoreService.collection(
        this.collection_pratiche_scuola,
      );
      const documentRef = collectionRef.doc(this.pratica.cui.valueOf());
      documentRef.get().subscribe((res) => {
        this.praticaFromFirestore = res.data() as PraticaFlowdy2_Firestore;
        this.scuolaForm
          .get("latitudineScuola")
          .setValue(this.praticaFromFirestore.coordinateScuola.latitude);
        this.scuolaForm
          .get("longitudineScuola")
          .setValue(this.praticaFromFirestore.coordinateScuola.longitude);
      });
    });
  }
  openMappaCentrale() {
    this.logger.info("---OPENMAPPA CENTRALE----");
    /*  this.ref = this.dialogService.open(DialogModificaPosizioneBando1, {
          data: {
            pratica: this.praticaFromFirestore,
            isScuola:false,
          },
          header: "Posizione della centrale",
          width: "70%",
          height: "75%",
          baseZIndex: 10000,
          closable: true,
          draggable: false,
        });
        this.ref.onClose.subscribe((changeResult) => {
          this.logger.info("CHIUDO MAPPA "+ changeResult);
          const collectionRef = this.firestoreService.collection(this.collection_pratiche_scuola);
          const documentRef   = collectionRef.doc(this.pratica.codiceInfratel.valueOf());
          documentRef.get().subscribe(res => {
            this.praticaFromFirestore =  res.data() as PraticaScuola_Firestore;
            this.centraleForm.get("latitudineCentrale").setValue(this.praticaFromFirestore.coordinateCentrale.latitude);
            this.centraleForm.get("longitudineCentrale").setValue(this.praticaFromFirestore.coordinateCentrale.longitude);
          });
        }); */
  }
  loadComuni() {
    this.comuniService.get().subscribe((res) => {
      this.comuni = res;
      //this.filteredComuni = res;
      this.comuniLoaded = true;
    });
  }
  filterComune(event: AutoCompleteCompleteEvent) {
    let filtered: any[] = [];
    let query = event.query;

    for (let i = 0; i < this.comuni.length; i++) {
      let comune = this.comuni[i];
      if (
        comune.denominazione.toLowerCase().indexOf(query.toLowerCase()) == 0
      ) {
        filtered.push(comune);
      }
    }
    this.filteredComuni = filtered;
  }
  loadCentrali() {
    this.centraliService.get().subscribe((res) => {
      this.centrali = res;
      this.centraliLoaded = true;
      this.centraleForm.get("idCentrale")?.enable();
    });
  }
  filterCentrali(event: AutoCompleteCompleteEvent) {
    let filtered: any[] = [];
    let query = event.query;

    for (let i = 0; i < this.centrali.length; i++) {
      let centrale = this.centrali[i];
      if (
        centrale.clliAttestazione.toLowerCase().indexOf(query.toLowerCase()) ==
        0
      ) {
        filtered.push(centrale);
      }
    }
    this.filteredCentrali = filtered;
  }
  loadImprese() {
    this.aziendeService.getByBando("1").subscribe((aziende) => {
      this.aziende = aziende;
    });
  }
  filterAzienda(event: AutoCompleteCompleteEvent) {
    let filtered: any[] = [];
    let query = event.query;

    for (let i = 0; i < this.aziende.length; i++) {
      let azienda = this.aziende[i];
      if (
        azienda.ragioneSociale.toLowerCase().indexOf(query.toLowerCase()) == 0
      ) {
        filtered.push(azienda);
      }
    }
    this.filteredAziende = filtered;
  }

  onCentraleSelect(event: any): void {
    let nuovaCentrale: Centrale = event.value;
    console.log(nuovaCentrale);

    this.centraleForm.get("idCentrale").setValue(nuovaCentrale.clli);
    this.centraleForm.get("nomeCentrale").setValue(nuovaCentrale.denominazione);
    this.centraleForm
      .get("indirizzoCentrale")
      .setValue(nuovaCentrale.indirizzo);
    this.centraleForm.get("comuneCentrale").setValue(nuovaCentrale.comune);
    this.centraleForm
      .get("latitudineCentrale")
      .setValue(nuovaCentrale.latitudine);
    this.centraleForm
      .get("longitudineCentrale")
      .setValue(nuovaCentrale.longitudine);
  }
  conferma(conferma: boolean) {
    if (!conferma) {
      this.dialogRef.close({ success: false });
    } else {
      let praticaDaModificare: ModificaPraticaDTO = new ModificaPraticaDTO();
      const tipoSedeSelezionataCode = this.getTipoSedeCode(
        this.dettagliForm.value.tipoSede,
      );
      const tipoSedeEffettivaSelezionataCode = this.getTipoSedeCode(
        this.dettagliForm.value.tipoSedeEffettiva,
      );
      this.logger.info("[MODIFICA PRATICA] Conferma - valori sede da form:", {
        tipoSedeRaw: this.dettagliForm.value.tipoSede,
        tipoSedeEffettivaRaw: this.dettagliForm.value.tipoSedeEffettiva,
        tipoSedeCode: tipoSedeSelezionataCode,
        tipoSedeEffettivaCode: tipoSedeEffettivaSelezionataCode,
      });
      const tipoSedePayload = { codice: tipoSedeSelezionataCode };
      const tipoSedeEffettivaPayload = {
        codice: tipoSedeEffettivaSelezionataCode,
      };
      praticaDaModificare.id = this.pratica.id;
      praticaDaModificare.codiceInvio = this.dettagliForm.value.codiceInvio;
      praticaDaModificare.tipoSede = tipoSedePayload as any;
      praticaDaModificare.tipoSedeEffettiva = tipoSedeEffettivaPayload as any;
      praticaDaModificare.lavorabile = this.dettagliForm.value.lavorabile;
      praticaDaModificare.wifi = this.dettagliForm.value.wifi;
      praticaDaModificare.numeroAccessPoint =
        this.dettagliForm.value.numeroAccessPoint;

      if (this.pratica.bando.denominazione === "BANDO 1") {
        if (
          this.dettagliForm.value.tipoBackup &&
          this.dettagliForm.value.tipoBackup != "NO"
        ) {
          praticaDaModificare.tipoBackup =
            this.dettagliForm.value.tipoBackup.codice;
          praticaDaModificare.backup = true;
        } else {
          praticaDaModificare.tipoBackup = "";
          praticaDaModificare.backup = false;
        }
      } else {
        praticaDaModificare.backup = this.dettagliForm.value.backup;
      }

      praticaDaModificare.idScuola = this.scuola.id;
      praticaDaModificare.denominazioneScuola =
        this.scuolaForm.value.denominazioneScuola;
      praticaDaModificare.indirizzoScuola =
        this.scuolaForm.value.indirizzoScuola;
      praticaDaModificare.codiceMiur = this.scuolaForm.value.codiceMiur;
      praticaDaModificare.comuneScuola =
        this.scuolaForm.value.comuneScuola.denominazione;
      praticaDaModificare.latitudineScuola =
        this.scuolaForm.value.latitudineScuola;
      praticaDaModificare.longitudineScuola =
        this.scuolaForm.value.longitudineScuola;

      praticaDaModificare.idCentrale = this.centraleForm.value.idCentrale;
      /*   praticaDaModificare.nomeCentrale = this.centraleForm.value.nomeCentrale;
      praticaDaModificare.indirizzoCentrale = this.centraleForm.value.indirizzoCentrale;
      praticaDaModificare.comuneCentrale =  this.centraleForm.value.comuneCentrale.denominazione;
      praticaDaModificare.latitudineCentrale = this.centraleForm.value.latitudineCentrale;
      praticaDaModificare.longitudineCentrale = this.centraleForm.value.longitudineCentrale; */

      this.logger.info(praticaDaModificare);
      this.praticheScuolaService.modifica(praticaDaModificare).subscribe({
        next: (v) => {},
        error: (e) => {
          this.messageService.add({
            severity: "info",
            summary: "Errore",
            life: 10000,
            detail: "Errore in fase di MODIFICA della pratica",
          });
          this.dialogRef.close({ success: false });
        },
        complete: () => {
          this.logger.info("MODIFICA PRATICA OK!");
          this.dialogRef.close({ success: true });
        },
      });
    }
  }
}
